import { Calendar, X } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { setProfileViewing, setShowShareCalendar } from '../../../../Redux/store';
import { useCalendarShare } from '../../../../hooks/useCalendarShare';
import useProfileData from '../../../../hooks/useProfileData';
import { useState } from 'react';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import { sharedTo, sharedFrom } from '../../../../data/functions';


/**
 * CalendarShareButton: Displays a calendar share button in chat
 *  
 * - Purple background with light purple border
 * - Rounded corners, bold white text
 * - Calendar icon on right side
 * - Can be clickable (in chat) or view-only (in input area preview)
 * 
 * For full architecture documentation, see: docs/calendar_share_architecture.md
 */
function CalendarShareButton({
  clickable = true,
  message,
  attachment,
  onRemove,
  showRemove = false,
  onSetTab
}) {
  // Redux state and hooks
  const dispatch = useDispatch();
  const currentUserId = useSelector((s) => s.user.authUserId);
  const ownedShares = useSelector((s) => s.calendarShares.ownedShares);
  const receivedShares = useSelector((s) => s.calendarShares.receivedShares);
  const { acceptShare } = useCalendarShare();
  const { profile: otherUserProfile } = useProfileData(message?.sender_id);
  // Local state for dialogs
  const [confirmShare, setConfirmShare] = useState(null);

  // Extract sender ID from message
  const senderId = message?.sender_id;

  // Extract attachment type and requester ID
  // Handle both new format (calendar_share/calendar_request) and old format (calendar)
  const attachmentType = attachment.type === 'calendar'
    ? (attachment.data.ownerId === senderId ? 'calendar_share' : 'calendar_request')
    : attachment.type;
  const requesterId = attachment.data.requesterId || attachment.data.ownerId;

  // Remove attachment from preview (input area only)
  const handleRemove = (e) => {
    e.stopPropagation();
    if (onRemove) onRemove();
  };

  // In preview mode (no message), show a simplified button
  // This happens when the button is in the input area before sending
  if (!message) {
    // Determine label based on attachment type
    let buttonText = 'Calendar';
    if (attachmentType === 'calendar_share') {
      buttonText = 'Share Calendar';
    } else if (attachmentType === 'calendar_request') {
      buttonText = 'Request Calendar';
    }

    const buttonContent = (
      <button
        disabled={!clickable}
        className={`flex items-center gap-2 rounded-2xl border-2 px-4 py-2.5 font-semibold text-white transition-colors ${
          clickable ? 'hover:bg-purple-700 cursor-pointer' : 'cursor-default opacity-100'
        }`}
        style={{
          backgroundColor: '#9333ea',
          borderColor: 'rgba(147, 51, 234, 0.4)',
        }}
      >
        <span>{buttonText}</span>
        <Calendar className="h-4 w-4 flex-shrink-0" />
      </button>
    );

    // If showing remove button (for input area), wrap with X button
    if (showRemove && onRemove) {
      return (
        <div className="relative inline-block" style={{ paddingTop: '8px', paddingRight: '8px' }}>
          <button
            onClick={handleRemove}
            className="absolute right-0 top-0 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70"
            aria-label="Remove"
          >
            <X className="h-3 w-3" />
          </button>
          {buttonContent}
        </div>
      );
    }

    return buttonContent;
  }

  // Determine the other user ID in the chat (the other participant, not based on who sent the message)
  const otherUserId = Object.keys(message.read_by || {}).find(id => id !== currentUserId);

  // Determine if user is the requester
  const isRequester = requesterId === currentUserId;

  // Determine share status and button text/action based on scenarios
  let buttonText = '';
  let handleClickAction = null;

  if (attachmentType === 'calendar_share') {
    if (isRequester) {
      // This user sent their calendar to someone else
      const isAccepted = sharedTo(currentUserId, ownedShares);
      if (isAccepted) {
        buttonText = 'Calendar Shared';
        handleClickAction = () => dispatch(setShowShareCalendar(true));
      } else {
        buttonText = 'Sent Calendar (pending)';
        handleClickAction = null; // nothing
      }
    } else {
      // Someone else sent their calendar to this user
      const isAccepted = sharedFrom(currentUserId, receivedShares, otherUserId);
      if (isAccepted) {
        buttonText = `View ${otherUserProfile?.name || 'their'}'s Calendar`;
        handleClickAction = () => {
          console.log('[CalendarShareButton] VIEW BUTTON CLICKED - handleClickAction (calendar_share)');
          console.log('[CalendarShareButton] - message:', JSON.stringify(message, null, 2));
          console.log('[CalendarShareButton] - message.id:', message.id);
          console.log('[CalendarShareButton] - message.attachments:', JSON.stringify(message.attachments, null, 2));
          console.log('[CalendarShareButton] - attachment:', JSON.stringify(attachment, null, 2));
          console.log('[CalendarShareButton] - attachment.id:', attachment.id);
          console.log('[CalendarShareButton] - attachment.type:', attachment.type);
          console.log('[CalendarShareButton] - attachment.data:', JSON.stringify(attachment.data, null, 2));
          console.log('[CalendarShareButton] - attachmentType:', attachmentType);
          console.log('[CalendarShareButton] - requesterId:', requesterId);
          console.log('[CalendarShareButton] - otherUserId:', otherUserId);
          console.log('[CalendarShareButton] - currentUserId:', currentUserId);
          console.log('[CalendarShareButton] - ownedShares:', JSON.stringify(ownedShares, null, 2));
          console.log('[CalendarShareButton] - receivedShares:', JSON.stringify(receivedShares, null, 2));
          console.log('[CalendarShareButton] - sharedFrom result:', sharedFrom(currentUserId, receivedShares, otherUserId));
          console.log('[CalendarShareButton] - otherUserProfile:', JSON.stringify(otherUserProfile, null, 2));
          if (onSetTab) {
            // Use callback to set tab if available (e.g., in ProfileFull)
            onSetTab('calendar');
          } else {
            // Fallback to Redux for other contexts
            dispatch(setProfileViewing({ id: otherUserId, tab: 'calendar' }));
          }
        };
      } else {
        buttonText = `Accept ${otherUserProfile?.name || 'their'}'s Calendar`;
        handleClickAction = () => {
          setConfirmShare({
            ownerId: otherUserId,
            recipientId: currentUserId,
            message: `Accept Share of ${otherUserProfile?.name || 'their'}'s Calendar?`,
            confirmLabel: 'Accept',
          });
        };
      }
    }
  } else if (attachmentType === 'calendar_request') {
    if (isRequester) {
      // This user requested someone else's calendar
      const isAccepted = sharedFrom(currentUserId, receivedShares, otherUserId);
      if (isAccepted) {
        buttonText = `View ${otherUserProfile?.name || 'their'}'s Calendar`;
        handleClickAction = () => {
          if (onSetTab) {
            // Use callback to set tab if available (e.g., in ProfileFull)
            onSetTab('calendar');
          } else {
            // Fallback to Redux for other contexts
            dispatch(setProfileViewing({ id: otherUserId, tab: 'calendar' }));
          }
        };
      } else {
        buttonText = `${otherUserProfile?.name || 'their'}'s Calendar Requested (pending)`;
        handleClickAction = null; // nothing
      }
    } else {
      // Someone else requested this user's calendar
      const isAccepted = sharedTo(currentUserId, ownedShares);
      if (isAccepted) {
        buttonText = 'Calendar Shared';
        handleClickAction = () => dispatch(setShowShareCalendar(true));
      } else {
        buttonText = 'Share Your Calendar';
        handleClickAction = () => {
          setConfirmShare({
            ownerId: currentUserId,
            recipientId: otherUserId,
            message: `Share your calendar with ${otherUserProfile?.name || 'them'}?`,
            confirmLabel: 'Share',
          });
        };
      }
    }
  } else {
    // Fallback for unknown attachment type
    console.error('[CalendarShareButton] Unknown attachment type:', attachment.type);
    buttonText = 'Calendar';
  }

  // Handle click
  const handleClick = async () => {
    if (!clickable || !handleClickAction) return;
    handleClickAction();
  };

  // Execute calendar share acceptance
  const handleConfirmShare = async () => {
    const result = await acceptShare(confirmShare.ownerId, confirmShare.recipientId);
    if (result?.error) {
      console.error('[CalendarShareButton] acceptShare failed:', result.error);
    }
    setConfirmShare(null);
  };

  // Render main button with purple styling
  const buttonContent = (
    <button
      onClick={handleClick}
      disabled={!clickable}
      className={`flex items-center gap-2 rounded-2xl border-2 px-4 py-2.5 font-semibold text-white transition-colors ${
        clickable ? 'hover:bg-purple-700 cursor-pointer' : 'cursor-default opacity-100'
      }`}
      style={{
        backgroundColor: '#9333ea',
        borderColor: 'rgba(147, 51, 234, 0.4)',
      }}
    >
      <span>{buttonText}</span>
      <Calendar className="h-4 w-4 flex-shrink-0" />
    </button>
  );

  // If showing remove button (for input area), wrap with X button
  if (showRemove && onRemove) {
    return (
      <div className="relative inline-block" style={{ paddingTop: '8px', paddingRight: '8px' }}>
        <button
          onClick={handleRemove}
          className="absolute right-0 top-0 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70"
          aria-label="Remove"
        >
          <X className="h-3 w-3" />
        </button>
        {buttonContent}
      </div>
    );
  }

  return (
    <>
      {/* Main calendar share button */}
      {buttonContent}
      {/* Confirmation dialog for accepting share */}
      {confirmShare && (
        <ConfirmDialog
          message={confirmShare.message}
          confirmLabel={confirmShare.confirmLabel}
          onConfirm={handleConfirmShare}
          onCancel={() => setConfirmShare(null)}
        />
      )}
    </>
  );
}

export default CalendarShareButton;
